import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Button, Card, Input, Select, DatePicker, Skeleton, Typography, Space, Row, Col } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { toast } from "../../components/toasts/toastStore";
import { usePermisos } from "../../context/PermisosContext";
import { apiCargosInstance, authHeaderCargos } from "../../redux/actions/cargos/cargos";

import "./PacientesPage.css";

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

const BANNER_BG = "linear-gradient(135deg, var(--brand-dark, #3d5590) 0%, var(--brand-primary) 100%)";

const money = (n) =>
  n == null || n === "" ? "—" : `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function CargosPage() {
  const navigate = useNavigate();
  const { perm } = usePermisos() || { perm: () => true };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState(null);
  const [loadingKpis, setLoadingKpis] = useState(true);

  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [soloPendientes, setSoloPendientes] = useState(false);
  const [rango, setRango] = useState(null);
  const [kpiActivo, setKpiActivo] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const fechaDesde = rango?.[0] ? rango[0].format("YYYY-MM-DD") : undefined;
  const fechaHasta = rango?.[1] ? rango[1].format("YYYY-MM-DD") : undefined;

  const fetchCargos = useCallback(() => {
    setLoading(true);
    apiCargosInstance
      .get("/cargos", {
        headers: authHeaderCargos(),
        params: {
          fecha_desde: fechaDesde,
          fecha_hasta: fechaHasta,
          solo_pendientes: soloPendientes || undefined,
        },
      })
      .then((res) => setItems(Array.isArray(res.data) ? res.data : res.data?.items ?? []))
      .catch(() => toast.error("No se pudieron cargar los cargos"))
      .finally(() => setLoading(false));
  }, [fechaDesde, fechaHasta, soloPendientes]);

  const fetchKpis = useCallback(() => {
    setLoadingKpis(true);
    apiCargosInstance
      .get("/cargos/kpis", {
        headers: authHeaderCargos(),
        params: { fecha_desde: fechaDesde, fecha_hasta: fechaHasta },
      })
      .then((res) => setKpis(res.data || {}))
      .catch(() => {})
      .finally(() => setLoadingKpis(false));
  }, [fechaDesde, fechaHasta]);

  useEffect(() => { fetchCargos(); }, [fetchCargos]);
  useEffect(() => { fetchKpis(); }, [fetchKpis]);

  const filteredItems = useMemo(() => {
    if (!searchDebounced) return items;
    const q = searchDebounced.toLowerCase();
    return items.filter((c) => {
      const paciente = (c.paciente_nombre || "").toLowerCase();
      const concepto = (c.concepto || "").toLowerCase();
      return paciente.includes(q) || concepto.includes(q) || String(c.id ?? c.id_cargo).includes(q);
    });
  }, [items, searchDebounced]);

  // Un solo KPI activo a la vez: pendiente y abiertos comparten el filtro
  // "solo pendientes" pero se resalta unicamente el que se pulso.
  const toggleKpiPendientes = (key) => {
    if (kpiActivo === key) {
      setSoloPendientes(false);
      setKpiActivo(null);
    } else {
      setSoloPendientes(true);
      setKpiActivo(key);
    }
  };

  const STAT_CARDS = [
    {
      key: "cobrado_mes",
      label: "Total cobrado del mes",
      icon: <i className="fa-solid fa-sack-dollar" />,
      iconCls: "paq-stat-icon-correo",
      value: money(kpis?.total_cobrado_mes),
      onClick: () => { setSoloPendientes(false); setRango(null); setKpiActivo("cobrado_mes"); },
    },
    {
      key: "pendiente",
      label: "Total pendiente",
      icon: <i className="fa-solid fa-hourglass-half" />,
      iconCls: "paq-stat-icon-menores",
      value: money(kpis?.total_pendiente),
      onClick: () => toggleKpiPendientes("pendiente"),
    },
    {
      key: "abiertos",
      label: "Cargos abiertos",
      icon: <i className="fa-solid fa-folder-open" />,
      iconCls: "paq-stat-icon-primario",
      value: kpis?.cargos_abiertos ?? "—",
      onClick: () => toggleKpiPendientes("abiertos"),
    },
    {
      key: "del_mes",
      label: "Cargos del mes",
      icon: <i className="fa-solid fa-calendar-check" />,
      iconCls: "paq-stat-icon-primario",
      value: kpis?.cargos_del_mes ?? "—",
      onClick: () => {
        setRango([dayjs().startOf("month"), dayjs().endOf("month")]);
        setSoloPendientes(false);
        setKpiActivo("del_mes");
      },
    },
  ];

  return (
    <main className="paq-main">
      <div className="paq-content">
        <section className="paq-section">
          <div className="paq-filters-panel">
            <Space direction="vertical" size={2} style={{ marginBottom: 16 }}>
              <Title level={2} className="paq-title">Cargos y Pagos</Title>
              <Text className="paq-subtitle">Historial de cargos generados a pacientes y sus pagos.</Text>
            </Space>

            <Row gutter={[16, 14]}>
              <Col xs={24} lg={10}>
                <div className="paq-field-label">Buscador</div>
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por paciente, concepto o folio..."
                  suffix={<SearchOutlined className="paq-input-suffix" />}
                  className="paq-control"
                  allowClear
                />
              </Col>
              <Col xs={24} md={12} lg={8}>
                <div className="paq-field-label">Periodo</div>
                <RangePicker
                  className="paq-control"
                  value={rango}
                  onChange={(v) => { setRango(v); setKpiActivo(null); }}
                  format="DD/MM/YYYY"
                  allowClear
                />
              </Col>
              <Col xs={24} md={12} lg={6}>
                <div className="paq-field-label">Solo pendientes</div>
                <Select
                  className="paq-control"
                  value={soloPendientes ? "pendientes" : "todos"}
                  onChange={(v) => { setSoloPendientes(v === "pendientes"); setKpiActivo(null); }}
                  options={[
                    { value: "todos", label: "Todos los cargos" },
                    { value: "pendientes", label: "Solo pendientes" },
                  ]}
                />
              </Col>
            </Row>

            <Row gutter={[16, 14]} style={{ marginTop: 14 }} align="bottom">
              <Col xs={24} lg={6}>
                <div className="paq-actions">
                  <Button
                    className="paq-btn-clean"
                    onClick={() => {
                      setSearch("");
                      setRango(null);
                      setSoloPendientes(false);
                      setKpiActivo(null);
                    }}
                  >
                    Limpiar filtros
                  </Button>
                </div>
              </Col>
            </Row>
          </div>

          <div className="paq-stats-row" style={{ gridTemplateColumns: `repeat(${STAT_CARDS.length}, minmax(0, 1fr))` }}>
            {STAT_CARDS.map((c) => (
              <Card
                key={c.key}
                className={`paq-stat-card ${kpiActivo === c.key ? "paq-stat-active" : ""}`}
                hoverable
                onClick={c.onClick}
              >
                <Space align="center" size={10}>
                  <div className={`paq-stat-icon ${c.iconCls}`}>{c.icon}</div>
                  <div>
                    <div className="paq-stat-value">{loadingKpis ? "…" : c.value}</div>
                    <div className="paq-stat-label">{c.label}</div>
                  </div>
                </Space>
              </Card>
            ))}
          </div>

          <div className="paq-expedientes-card">
            <div className="paq-toolbar">
              <div className="paq-toolbar-left">
                <Title level={4} style={{ marginBottom: 0 }}>Cargos ({filteredItems.length})</Title>
                <Text type="secondary">{filteredItems.length} cargos encontrados</Text>
              </div>
              <div className="paq-toolbar-right">
                {perm("cargos_pagos", "insertar") && (
                  <Button
                    type="primary"
                    icon={<i className="fa-solid fa-plus" />}
                    onClick={() => navigate("/app/cargos/nuevo")}
                    style={{ backgroundColor: "var(--brand-primary)", borderColor: "var(--brand-primary)" }}
                  >
                    Nuevo cargo
                  </Button>
                )}
              </div>
            </div>

            {!loading && filteredItems.length === 0 && (
              <div className="paq-empty">
                <div style={{ fontSize: 16, fontWeight: 500 }}>Sin cargos que coincidan con los filtros</div>
              </div>
            )}

            <div className="paq-grid">
              {loading &&
                Array.from({ length: 8 }).map((_, i) => (
                  <div key={`sk-${i}`} className="paq-card paq-card-skeleton">
                    <div className="paq-card-header">
                      <Skeleton active title={{ width: "60%" }} paragraph={{ rows: 1, width: "40%" }} />
                    </div>
                    <div className="paq-card-body">
                      <Skeleton active title={false} paragraph={{ rows: 3 }} />
                    </div>
                  </div>
                ))}

              {!loading &&
                filteredItems.map((c) => {
                  const id = c.id ?? c.id_cargo;
                  const saldo = c.saldo ?? Math.max(0, Number(c.monto_base || 0) - Number(c.total_pagado || 0));
                  const pendiente = parseFloat(saldo || 0) > 0;
                  return (
                    <div key={id} className="paq-card">
                      <div className="paq-card-banner" style={{ background: BANNER_BG }}>
                        <i className="fa-solid fa-file-invoice-dollar paq-card-banner-icon" />
                      </div>

                      <div className="paq-card-header">
                        <div className="paq-card-head">
                          <Paragraph className="paq-card-name" ellipsis={{ rows: 2 }}>
                            {c.paciente_nombre || `Paciente #${c.id_paciente}`}
                          </Paragraph>
                          <span className={`paq-tipo-badge ${pendiente ? "paq-tipo-pendiente" : "paq-tipo-activo"}`}>
                            {pendiente ? "Pendiente" : "Liquidado"}
                          </span>
                        </div>
                      </div>

                      <div className="paq-card-body">
                        <div className="paq-contenidos-box">
                          <i className="fa-solid fa-file-lines paq-info-icon" />
                          <div className="paq-info-text">
                            <span className="paq-info-label">Concepto</span>
                            <span className="paq-info-value">{c.concepto || "Sin concepto"}</span>
                          </div>
                        </div>
                        <div className="paq-contenidos-box">
                          <i className="fa-solid fa-dollar-sign paq-info-icon" />
                          <div className="paq-info-text">
                            <span className="paq-info-label">Monto</span>
                            <span className="paq-info-value">{money(c.monto_base)}</span>
                          </div>
                        </div>
                        <div className="paq-contenidos-box">
                          <i className="fa-solid fa-hand-holding-dollar paq-info-icon" />
                          <div className="paq-info-text">
                            <span className="paq-info-label">Pagado · Saldo</span>
                            <span className="paq-info-value">
                              {money(c.total_pagado)} · <span style={{ color: pendiente ? "#92400e" : "#065f46" }}>{money(saldo)}</span>
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="paq-card-footer">
                        <Button
                          className="paq-btn-details"
                          icon={<i className="fa-solid fa-circle-info" />}
                          onClick={() => navigate(`/app/cargos/${id}`)}
                        >
                          Ver detalles
                        </Button>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
